import { Col, Icon, Spin } from 'antd'
import { useState, FC } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import shallow from 'zustand/shallow'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerButton from '../../../components/custom_components/Button'
import PerDropzone from '../../../components/custom_components/DragAndDrop'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { importExcel } from '../../../graphql/talents-season/talentsSeasonQueries'
import { ExcelManager } from '../../../shared/xlsx/ExcelManager'
import { EmployeeWithRankAndTags, ExcelDataInput, TalentsSeasonStatus } from '../../../types'
import { useCultivatingTalentsStore } from '../../cultivatingTalents/CultivatingTalentsStore'
import '../../../components/custom_components/Text/index.less'
import { useHRSettingsStore } from '../hrSettings/HRSettingsStore'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import { TFunction } from 'i18next'
import { showNotification } from '../../../components/custom_components/Notification/notification'

export type RankingEditImportExportBoxProps = {
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
  isHRSettings: boolean
  isCompanyManager: boolean
}

export type IsProcessingFile = {
  active: boolean
  isProcessing?: boolean
  text?: string
  errorMessage?: string
}

export const RankingEditImportExportBox: FC<RankingEditImportExportBoxProps> = ({
  isAllCompaniesSettings,
  isHRSettings,
  isYourCompanyView,
  isCompanyManager,
}) => {
  const { t } = useTranslation()
  const [showModal, toggleModal] = useState<boolean>(false)
  const [dropZoneError, setDropZoneError] = useState<string | undefined>(undefined)
  const [dropZoneProcessing, setDropZoneProcessing] = useState<IsProcessingFile>({
    active: false,
    isProcessing: false,
    text: undefined,
    errorMessage: undefined,
  })
  const [filename, setFilename] = useState<string | undefined>(undefined)
  const [isConfirmingImportData, toggleConfirmingImportData] = useState<boolean>(false)
  const [importedData, setImportedData] = useState<EmployeeWithRankAndTags[] | undefined>(undefined)
  const [guidelinesModalVisible, setGuidelinesModalVisible] = useState<boolean>(false)

  const MAX_ALLOWED_FILE_SIZE = 1048576

  const [
    rankingEditingInProgress,
    toggleRankingEditingInProgress,
    talentsSeasonSettings,
    companiesAndCountriesSelected,
    selectedFromTalentsSeason,
    employees,
    setRankedEmployees,
    toggleSortRanking,
  ] = useCultivatingTalentsStore(
    (state) => [
      state.rankingEditingInProgress,
      state.toggleRankingEditingInProgress,
      state.talentsSeasonSettings,
      state.companiesAndCountriesSelected,
      state.selected,
      [...state.employees].sort((x, y) => x.rank - y.rank),
      state.setRankedEmployees,
      state.toggleSortRanking,
    ],
    shallow,
  )

  const [selectedFromHRSettings] = useHRSettingsStore((state) => [state.selected])
  const [selectedFromCompanySettings] = useCompanySettingsStore((state) => [state.selected])

  if (!talentsSeasonSettings) {
    return null
  }

  const { companyId, country, year, talentsSeasonStatus } = talentsSeasonSettings

  const headerColumnNames = [
    t('rankingView.excel.fileColumnNames.id'),
    t('rankingView.excel.fileColumnNames.currentRanking'),
    t('rankingView.excel.fileColumnNames.lastYearRanking'),
    t('rankingView.excel.fileColumnNames.assessable'),
    t('rankingView.excel.fileColumnNames.firstName'),
    t('rankingView.excel.fileColumnNames.lastName'),
    t('rankingView.excel.fileColumnNames.title'),
    t('rankingView.excel.fileColumnNames.bu'),
    t('rankingView.excel.fileColumnNames.currentEvaluation'),
    t('rankingView.excel.fileColumnNames.proposal'),
    t('rankingView.excel.fileColumnNames.lastConfirmedEvaluation'),
    t('rankingView.excel.fileColumnNames.performance'),
  ].filter((el) =>
    talentsSeasonStatus === TalentsSeasonStatus.Closed
      ? ![t('rankingView.excel.fileColumnNames.lastConfirmedEvaluation')].includes(el)
      : true,
  )

  const antIcon = (
    <Icon
      type={dropZoneProcessing.isProcessing ? 'loading' : dropZoneProcessing.errorMessage ? 'exclamation' : 'check'}
      style={{
        fontSize: 20,
        color: dropZoneProcessing.isProcessing ? '#A3A9AD' : dropZoneProcessing.errorMessage ? '#FF6259' : '#FFFFFF',
        display: 'flex',
      }}
      spin={dropZoneProcessing.isProcessing ? true : false}
    />
  )

  const downloadExcel = () => {
    let filename = 'Cultivating Talents'
    let companyName = ''
    const country =
      isAllCompaniesSettings || isYourCompanyView
        ? selectedFromCompanySettings.country
        : isHRSettings
        ? selectedFromHRSettings.country
        : selectedFromTalentsSeason.country
    if (isHRSettings || isAllCompaniesSettings || isYourCompanyView) {
      companyName =
        isAllCompaniesSettings || isYourCompanyView ? selectedFromCompanySettings.companyName : selectedFromHRSettings.companyName
    } else {
      for (const el of companiesAndCountriesSelected) {
        if (el.value === selectedFromTalentsSeason.companyId && el.label) {
          companyName = el.label.toString()
          break
        }
      }
    }
    if (companyName && companyName !== '') {
      filename = filename.concat(' - ').concat(companyName)
    }
    if (country) {
      filename = filename.concat(' - ').concat(country)
    }
    if (year) {
      filename = filename.concat(' - ').concat(String(year))
    }
    filename = filename.concat('.xlsx')
    /* original data */
    const data = mapRankingEmployeesToArray(employees)
    const wsName = String(year)

    ExcelManager.getInstance().downloadExcel(filename, data, wsName)
  }

  const mapRankingEmployeesToArray = (sortedData: EmployeeWithRankAndTags[]) => {
    const res: (string | number)[][] = []
    const header = headerColumnNames
    res.push(header)
    sortedData.map((el) => {
      let tags = ''
      el.tags.map((tag) => {
        const tagValue = t(`global.employeeTags.general.${tag.type}`)
        tags = tags.concat(tagValue).concat(', ')
        return true
      })
      tags = tags.substring(0, tags.length - 2)
      if (talentsSeasonStatus !== TalentsSeasonStatus.Closed) {
        res.push([
          el.employee.id,
          el.assessable ? el.rank : undefined,
          el.previousRank ? el.previousRank : '-',
          el.assessable ? 1 : 0,
          el.employee.firstName,
          el.employee.lastName,
          el.employee.currentLevel,
          el.employee.buName,
          el.draftEvaluation ? el.draftEvaluation.toFixed(2) : el.evaluation ? el.evaluation.toFixed(2) : undefined,
          el.draftEvaluation ? 'X' : undefined,
          el.latestEvaluation ? el.latestEvaluation.toFixed(2) : undefined,
          tags,
        ])
      } else {
        res.push([
          el.employee.id,
          el.assessable ? el.rank : undefined,
          el.previousRank ? el.previousRank : '-',
          el.assessable ? 1 : 0,
          el.employee.firstName,
          el.employee.lastName,
          el.employee.currentLevel,
          el.employee.buName,
          el.draftEvaluation ? el.draftEvaluation.toFixed(2) : el.evaluation ? el.evaluation.toFixed(2) : undefined,
          el.draftEvaluation ? 'X' : undefined,
          tags,
        ])
      }
      return true
    })
    return res
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDrop = (uploadedfile: any) => {
    let hasError = false
    if (!uploadedfile) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noFile'))
    } else if (!uploadedfile.type) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noType'))
    } else if (!uploadedfile.path) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noPath'))
    } else {
      const extension = uploadedfile.path.substring(uploadedfile.path.lastIndexOf('.') + 1, uploadedfile.path.length)
      if (extension !== 'xlsx') {
        hasError = true
        setDropZoneError(t('rankingView.excel.importModal.errors.extension'))
      } else {
        importExcelFile(uploadedfile)
      }
    }
    if (hasError) {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const importExcelFile = async (uploadedFile: any) => {
    let importedFileAsJson: string[]

    try {
      importedFileAsJson = await ExcelManager.getInstance().importExcelFile(uploadedFile)
    } catch (err) {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
        errorMessage: t('rankingView.excel.importModal.errors.unknown'),
      })
      return
    }

    setDropZoneProcessing({
      active: true,
      isProcessing: true,
      text: t('rankingView.excel.importModal.processingMessage'),
    })

    let transformedFilename = uploadedFile.path
    const indexOfSlash = uploadedFile.path.indexOf('/')
    if (indexOfSlash >= 0) {
      transformedFilename = transformedFilename.substring(indexOfSlash + 1)
    }

    setFilename(transformedFilename)

    const refactoredExcelData: ExcelDataInput[] | string = refactorExcelData(JSON.parse(importedFileAsJson[0]))
    if (typeof refactoredExcelData === 'string') {
      setDropZoneProcessing({
        active: true,
        isProcessing: false,
        errorMessage: refactoredExcelData,
      })
      return
    }

    importExcel(companyId, country, year, refactoredExcelData)
      .then((res) => {
        if (res.errors && !!res.errors.length) {
          if (res.errors[0].extensions && res.errors[0].extensions.validationErrors.length > 0) {
            for (const error of res.errors[0].extensions.validationErrors) {
              if (error.field && error.field === 'importExcel' && error.message) {
                setDropZoneProcessing({
                  active: true,
                  isProcessing: false,
                  errorMessage: error.message,
                })
                return
              }
            }
          }
          if (res.errors[0].message) {
            setDropZoneProcessing({
              active: true,
              isProcessing: false,
              errorMessage: res.errors[0].message,
            })
          } else {
            setDropZoneProcessing({
              active: true,
              isProcessing: false,
              errorMessage: t('rankingView.excel.importModal.errors.unknown'),
            })
          }
        } else {
          setImportedData([...res.data.importExcel] as EmployeeWithRankAndTags[])
          setDropZoneProcessing({
            active: true,
            isProcessing: false,
            text: t('rankingView.excel.importModal.successMessage'),
          })
        }
      })
      .catch(() => {
        setDropZoneProcessing({
          active: true,
          isProcessing: false,
          errorMessage: t('rankingView.excel.importModal.errors.unknown'),
        })
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refactorExcelData = (jsonObject: any): ExcelDataInput[] | string => {
    const res: ExcelDataInput[] = []
    const utilColumnHeaders: string[] = [
      t('rankingView.excel.fileColumnNames.id').toLocaleUpperCase(),
      t('rankingView.excel.fileColumnNames.assessable').toLocaleUpperCase(),
      t('rankingView.excel.fileColumnNames.currentRanking').toLocaleUpperCase(),
    ]
    for (let i = 0; i < jsonObject.length; i++) {
      const headerNames: string[] = Object.keys(jsonObject[i]).filter((cn) => utilColumnHeaders.includes(cn.toLocaleUpperCase()))
      if (headerNames.length > utilColumnHeaders.length) {
        return t('rankingView.excel.importModal.errors.duplicatedColumn')
      }
      const idHeaderName: string | undefined = headerNames.find(
        (key: string) => key.toLocaleUpperCase() === t('rankingView.excel.fileColumnNames.id').toLocaleUpperCase(),
      )
      const assessableHeaderName: string | undefined = headerNames.find(
        (key: string) => key.toLocaleUpperCase() === t('rankingView.excel.fileColumnNames.assessable').toLocaleUpperCase(),
      )
      const currentRankingHeaderName: string | undefined = headerNames.find(
        (key: string) => key.toLocaleUpperCase() === t('rankingView.excel.fileColumnNames.currentRanking').toLocaleUpperCase(),
      )
      if (!idHeaderName || !jsonObject[i][idHeaderName]) {
        return t('rankingView.excel.importModal.errors.noId', { rowNo: i + 2 })
      }
      if (!assessableHeaderName || [null, undefined, ''].includes(jsonObject[i][assessableHeaderName])) {
        return t('rankingView.excel.importModal.errors.noAssessableValue', { rowNo: i + 2 })
      }
      if (!['0', 0, '1', 1].includes(jsonObject[i][assessableHeaderName])) {
        return t('rankingView.excel.importModal.errors.wrongAssessableValue', {
          value: jsonObject[i][assessableHeaderName],
          rowNo: i + 2,
        })
      }
      if (
        ['1', 1].includes(jsonObject[i][assessableHeaderName]) &&
        (!currentRankingHeaderName || !jsonObject[i][currentRankingHeaderName])
      ) {
        return t('rankingView.excel.importModal.errors.noRanking', { rowNo: i + 2 })
      }
      if (
        !!currentRankingHeaderName &&
        !!jsonObject[i][currentRankingHeaderName] &&
        !/^\d+$/.test(jsonObject[i][currentRankingHeaderName])
      ) {
        return t('rankingView.excel.importModal.errors.rankingNaN', {
          value: jsonObject[i][currentRankingHeaderName],
          rowNo: i + 2,
        })
      }

      res.push({
        employeeId: jsonObject[i][idHeaderName],
        rankingPosition:
          currentRankingHeaderName && jsonObject[i][currentRankingHeaderName]
            ? parseInt(jsonObject[i][currentRankingHeaderName], 10)
            : null,
        assessable: !jsonObject[i][assessableHeaderName]
          ? false
          : jsonObject[i][assessableHeaderName] === 'true' ||
            jsonObject[i][assessableHeaderName] === '1' ||
            jsonObject[i][assessableHeaderName] === 1
          ? true
          : false,
      })
    }
    return res
  }

  const handleConfirm = () => {
    toggleConfirmingImportData(true)
    if (importedData) {
      setRankedEmployees(importedData)
    }
    showNotification({
      type: 'success',
      message: t('rankingView.excel.importModal.notifications.success.title'),
      description: t('rankingView.excel.importModal.notifications.success.description'),
    })
    closeModal()
    toggleConfirmingImportData(false)
  }

  const closeModal = () => {
    setDropZoneError(undefined)
    setDropZoneProcessing({
      active: false,
    })
    setFilename(undefined)
    toggleModal(false)
  }

  return (
    <>
      <BackgroundBox rt height="editImportExport" noPadding noShadow>
        <PerFlexRow
          align="middle"
          justify={
            isHRSettings ||
            isAllCompaniesSettings ||
            isYourCompanyView ||
            talentsSeasonSettings.talentsSeasonStatus !== TalentsSeasonStatus.Opened
              ? 'end'
              : 'space-between'
          }
          noWrap
          className="fullHeight"
          mr="l"
          ml="l"
        >
          {!(isHRSettings || isAllCompaniesSettings || isYourCompanyView) &&
            isCompanyManager &&
            talentsSeasonSettings.talentsSeasonStatus === TalentsSeasonStatus.Opened && (
              <PerFlexRow direction="row" align="middle">
                <PerButton
                  text={t('companySettingsView.rankingTab.settings.editRanking')}
                  color="primary"
                  before="edit_fill"
                  disabled={talentsSeasonSettings.talentsSeasonStatus !== TalentsSeasonStatus.Opened || rankingEditingInProgress}
                  onClick={() => toggleRankingEditingInProgress(true)}
                />

                <PerButton
                  ml="xs"
                  text={t('companySettingsView.rankingTab.settings.guidelines.button')}
                  color="primary"
                  onClick={() => {
                    setGuidelinesModalVisible(true)
                  }}
                />

                {rankingEditingInProgress && (
                  <PerButton
                    text={t('rankingView.sortByEvaluationButton')}
                    color="success"
                    before="ranking_fill"
                    onClick={() => toggleSortRanking(true)}
                    ml="xs"
                  />
                )}
              </PerFlexRow>
            )}
          <PerFlexRow direction="row">
            {rankingEditingInProgress && (
              <PerButton text={t('global.button.import')} color="success" before="import_fill" onClick={() => toggleModal(true)} />
            )}
            {[TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed, TalentsSeasonStatus.Closed].includes(
              talentsSeasonSettings.talentsSeasonStatus,
            ) && (
              <PerButton
                text={t('global.button.export')}
                color="primary"
                before="download_fill"
                type="button"
                disabled={rankingEditingInProgress}
                onClick={downloadExcel}
                ml={rankingEditingInProgress ? 'xs' : undefined}
              />
            )}
          </PerFlexRow>
        </PerFlexRow>
      </BackgroundBox>

      <PerModal
        size="small"
        title={{
          heading: t('rankingView.excel.importModal.header'),
        }}
        centered
        visible={showModal}
        destroyOnClose={true}
        onCancel={closeModal}
        footer={{
          cancelBtn: {
            onClick: closeModal,
          },
          customBtns: [
            {
              text: t('rankingView.excel.importModal.confirmButton'),
              onClick: handleConfirm,
              color: 'success',
              icon: 'check_stroke',
              disabled: !dropZoneProcessing.active || dropZoneProcessing.isProcessing || dropZoneProcessing.errorMessage !== undefined,
              isProcessing: { active: isConfirmingImportData },
            },
          ],
        }}
      >
        <PerText className="import-excel-modal-description">{t('rankingView.excel.importModal.description')}</PerText>
        {(!dropZoneProcessing.active || dropZoneProcessing.errorMessage) && (
          <PerDropzone
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onFileDropped={(file: unknown): void => {
              setDropZoneError(undefined)
              onDrop(file)
            }}
            maxAllowedSize={MAX_ALLOWED_FILE_SIZE}
            footerText={t('rankingView.excel.importModal.formatMessage')}
            footerTextClassNames={['import-excel-modal-footer-text']}
            width="50%"
            errorText={dropZoneError}
            errorTextClassNames={['import-excel-modal-error-text']}
          />
        )}
        {(dropZoneProcessing.active || dropZoneProcessing.isProcessing) && (
          <PerFlexRow align="middle" direction="row" mt="s">
            <Col>
              <div
                style={{
                  marginRight: '18px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '16px',
                  background: dropZoneProcessing.isProcessing ? '#FFFFFF' : dropZoneProcessing.errorMessage ? '#FFDAD4' : '#88D22A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Spin className="appraisal-teaser-info" indicator={antIcon} />
              </div>
            </Col>
            <Col span={20} style={{ padding: 0 }}>
              {dropZoneProcessing.errorMessage && (
                <PerFlexRow direction="column">
                  <PerText className="import-excel-modal-loading-success-message-text">
                    {t('rankingView.excel.importModal.errors.message1')}
                  </PerText>
                  <PerText className="import-excel-modal-loading-success-message-text" mb="s">
                    {dropZoneProcessing.errorMessage}
                  </PerText>
                  <PerText className="import-excel-modal-loading-success-message-text">
                    {t('rankingView.excel.importModal.errors.message2')}
                  </PerText>
                </PerFlexRow>
              )}
              {!dropZoneProcessing.errorMessage && (
                <PerFlexRow direction="column">
                  <PerText className="import-excel-modal-loading-success-message-text">{dropZoneProcessing.text}</PerText>
                  <PerText className="import-excel-modal-loading-footer">{filename}</PerText>
                </PerFlexRow>
              )}
            </Col>
          </PerFlexRow>
        )}
      </PerModal>

      <GuidelinesModal t={t} visible={guidelinesModalVisible} closeModal={() => setGuidelinesModalVisible(false)} />
    </>
  )
}

const GuidelinesModal: FC<{ closeModal; visible: boolean; t: TFunction }> = ({ closeModal, visible, t }) => {
  return (
    <PerModal
      size="large"
      title={{
        heading: t('companySettingsView.rankingTab.settings.guidelines.heading'),
      }}
      centered
      visible={visible}
      destroyOnClose={true}
      onCancel={closeModal}
      footer={{
        cancelBtn: {
          onClick: closeModal,
        },
      }}
    >
      <PerText>{t('companySettingsView.rankingTab.settings.guidelines.d1')}</PerText>
      <PerText mt="xxs">{t('companySettingsView.rankingTab.settings.guidelines.d2')}</PerText>

      <PerText mt="s" appearance="tile-heading" mb="xxs">
        {t('companySettingsView.rankingTab.settings.guidelines.heading')}
      </PerText>
      <PerText>{t('companySettingsView.rankingTab.settings.guidelines.d3')}</PerText>
      <PerText mb="s">{t('companySettingsView.rankingTab.settings.guidelines.d4')}</PerText>

      <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel" components={[<b></b>]} />
      <ol>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel1" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel2" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel3" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel4" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.excel5" components={[<b></b>]} />
        </li>
      </ol>

      <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.evaluation" components={[<b></b>]} />
      <ol>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.evaluation1" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.evaluation2" components={[<b></b>]} />
        </li>
        <li>
          <Trans i18nKey="companySettingsView.rankingTab.settings.guidelines.evaluation3" components={[<b></b>]} />
        </li>
      </ol>
    </PerModal>
  )
}
